import { Box, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { Grid, Typography } from '@paintscout/ui'
import React from 'react'
import classNames from 'classnames'
import SWLink from '../SWLink'

const useStyles = makeStyles<Theme, ContentBlockProps>(
  (theme) => ({
    root: {
      padding: '50px',
      background: '#fff'
    },
    content: {
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center'
      }
    },
    preTitle: {
      marginBottom: '10px',
      fontFamily: 'CustomFont-SW',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '16px',
      letterSpacing: '1.5px',
      textTransform: 'uppercase'
    },
    title: {
      fontFamily: 'CustomFont-SW',
      fontSize: '44px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '48px',
      letterSpacing: '-1.5px',
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center'
      }
    },
    description: {
      marginTop: '10px',
      fontFamily: 'CustomFont-SW',
      fontSize: '20px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '-0.5px',

      [theme.breakpoints.down('sm')]: {
        textAlign: 'center'
      }
    },
    image: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      [theme.breakpoints.down('md')]: {
        justifyContent: 'center'
      }
    },
    centeredText: {
      paddingLeft: theme.spacing(9),
      [theme.breakpoints.down('lg')]: {
        paddingLeft: theme.spacing(7.5)
      },
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 0
      }
    },
    linksContainer: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px',
      marginLeft: `${theme.spacing(9)}px`,
      [theme.breakpoints.down('lg')]: {
        marginLeft: `${theme.spacing(7.5)}px`
      },
      [theme.breakpoints.down('sm')]: {
        marginLeft: `-${theme.spacing(0)}px`,
        padding: '0 15px'
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0
      }
    }
  }),
  { name: 'ContentBlock' }
)

export interface ContentBlockProps {
  img: string
  preTitle?: React.ReactNode
  title: React.ReactNode
  description: React.ReactNode
  linkHref?: string
  linkLabel?: string
  backgroundColor?: string
  imgFirst?: boolean
  appLinks?: {
    apple: { img: string; href: string }
    google: { img: string; href: string }
  }
  styles?: React.CSSProperties
}

function ContentBlock(props: ContentBlockProps) {
  const classes = useStyles(props)

  const { img, preTitle, title, description, linkHref, linkLabel, backgroundColor, imgFirst, styles, appLinks } = props

  return (
    <Box className={classes.root} style={{ background: backgroundColor, ...styles }}>
      <Grid container alignItems="center" direction={imgFirst ? 'row' : 'row-reverse'} spacing={2}>
        <Grid item xs={12} md={6} xl={6} className={classes.image}>
          <img src={img} style={{ maxWidth: '100%', maxHeight: '349px' }} />
        </Grid>
        <Grid item md={5} xl={6} container direction="column" className={classes.content}>
          {preTitle && (
            <Grid item>
              <Typography variant="h4" className={classes.preTitle} style={{ marginTop: '20px' }}>
                {preTitle}
              </Typography>
            </Grid>
          )}
          <Grid item className={classNames({ [classes.centeredText]: !!props.appLinks })}>
            <Typography variant="h1" className={classes.title}>
              {title}
            </Typography>
          </Grid>
          <Grid item className={classNames({ [classes.centeredText]: !!props.appLinks })}>
            <Typography variant="body1" className={classes.description}>
              {description}
            </Typography>
          </Grid>
          {!!linkHref && !!linkLabel && (
            <Grid item>
              <SWLink style={{ marginTop: '20px' }} href={linkHref} label={linkLabel} theme="dark" />
            </Grid>
          )}
          {appLinks && (
            <div className={classes.linksContainer}>
              <a href={appLinks.apple.href} target="_blank" rel="noreferrer">
                <img
                  src={appLinks.apple.img}
                  alt="Download on the App Store"
                  style={{ height: '50px', width: 'auto' }}
                />
              </a>
              <a href={appLinks.google.href} target="_blank" rel="noreferrer">
                <img src={appLinks.google.img} alt="Get it on Google Play" style={{ height: '50px', width: 'auto' }} />
              </a>
            </div>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default ContentBlock
